import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Observable, defer, forkJoin, of, throwError } from 'rxjs';
import { catchError, finalize, map, takeUntil, tap } from 'rxjs/operators';
import { LookupOption, LookupTypeCodes } from '../../../models/lookup.models';
import { MatterTaskDto } from '../../../models/matter.models';
import { NotesWithMentionsDto } from '../../../models/notes.models';
import { TaskAssignmentDto } from '../../../models/request.models';
import { AssignmentService } from '../../../services/assignment.service';
import { LookupService } from '../../../services/lookup.service';
import { MatterService } from '../../../services/matter.service';
import { CopyableTextComponent } from '../copyable-text/copyable-text.component';
import { NotesService } from '../../../services/notes.service';
import {
  SearchableMultiSelectComponent,
  SelectOption,
} from '../searchable-multi-select/searchable-multi-select.component';
import { SnackbarService } from '../../../services/snackbar.service';
import { TaskAssignmentListComponent } from '../task-assignment-list/task-assignment-list.component';
import { describeApiError, recoverNotFound } from '../../../utils/api-error.utils';
import { sortAssignmentStatusOptions } from '../../../utils/assignment-status.utils';
import { formatUserEditHistoryName } from '../../../utils/user-name.utils';
import {
  UnsavedChangesDialogComponent,
  UnsavedChangesDialogData,
} from '../unsaved-changes-dialog/unsaved-changes-dialog.component';

export interface AssignmentEditDialogData {
  assignedResourceId: number;
  requestId?: number;
  clientMatterNumber?: string;
  resourceName?: string;
  dialogTitle?: string;
  currentStatus: string;
  currentAssignmentType: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  notesInfo?: NotesWithMentionsDto | null;
  canEdit: boolean;
  canRemove: boolean;
  /** When false, hides the Manager Note section entirely. Defaults to true. */
  showManagerNotes?: boolean;
  /**
   * Whether the manager note may be written (Assignments.ManagerNotes.Write).
   * Separate from canEdit — which covers status, dates and type (Assignments.Edit) —
   * because the API gates the manager-note PUT/DELETE on the Write permission alone.
   * A user holding Read but not Write still SEES the note (showManagerNotes) as a
   * read-only field.
   */
  canEditManagerNotes?: boolean;
  /**
   * When true, shows the resource-note section (the assigned resource's own
   * note). Hidden entirely when false — viewers without access see no trace.
   */
  showResourceNotes?: boolean;
  /**
   * True when the resource note belongs to the current user. Writes are
   * ownership-only — there is no write permission — so this doubles as the flag
   * that the note reads as "mine" rather than someone else's.
   */
  canEditResourceNotes?: boolean;
  resourceNotesInfo?: NotesWithMentionsDto | null;
  /**
   * The tasks already assigned on this row. Seeds the section's list, which the dialog
   * then extends in place as creates land. Empty and undefined mean the same thing here
   * — no tasks yet — because whether the section shows at all is showTaskAssignments'
   * decision, not this field's.
   */
  taskAssignments?: TaskAssignmentDto[];
  /**
   * The `task-assignments` feature flag, resolved by the card. The Tasks section also
   * needs a clientMatterNumber (to read the matter's available tasks) and an
   * assignedResourceId (to hang them off), so all three gate it together.
   */
  showTaskAssignments?: boolean;
}

export interface AssignmentEditDialogResult {
  removed?: boolean;
  status?: string;
  statusName?: string;
  assignmentTypeCategory?: string;
  assignmentTypeCategoryName?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  notesInfo?: NotesWithMentionsDto | null;
  /** null = deleted, undefined = untouched (same convention as notesInfo). */
  resourceNotesInfo?: NotesWithMentionsDto | null;
  /**
   * The assignment's tasks as they stand after the save — the whole list, not a delta, so the
   * card can drop it straight onto the row. `undefined` = the dialog did not touch tasks;
   * an empty array is a real answer and means the assignment now has none.
   *
   * Set only when at least one row was created AND the server skipped none of the
   * submitted codes. A create whose codes were all skipped changes nothing, so reporting
   * the list would make the card repaint a row that has not moved. A PARTIAL skip is the
   * subtler case: the POST answers with only the rows it created, so a skipped code never
   * reaches this dialog and the list here is missing it — reporting that would push a
   * count onto the row that is lower than what the server holds. In both cases the dialog
   * reports `undefined` instead: stale, but never confidently wrong. Closing that window
   * properly needs a re-read, which the API does not expose today.
   */
  taskAssignments?: TaskAssignmentDto[];
}

@Component({
  selector: 'app-assignment-edit-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    CopyableTextComponent,
    SearchableMultiSelectComponent,
    TaskAssignmentListComponent,
  ],
  templateUrl: './assignment-edit-dialog.component.html',
  styleUrls: ['./assignment-edit-dialog.component.scss'],
})
export class AssignmentEditDialogComponent {
  private dialogRef =
    inject<
      MatDialogRef<
        AssignmentEditDialogComponent,
        AssignmentEditDialogResult | null
      >
    >(MatDialogRef);
  private dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private assignmentService = inject(AssignmentService);
  private lookupService = inject(LookupService);
  private matterService = inject(MatterService);
  private notesService = inject(NotesService);
  private snackbar = inject(SnackbarService);
  data = inject<AssignmentEditDialogData>(MAT_DIALOG_DATA);

  form: FormGroup;
  statusOptions: LookupOption[] = [];
  typeOptions: LookupOption[] = [];

  isSaving = false;

  /**
   * The assignment's tasks as they stand right now: seeded from the card's row and
   * extended in place as creates land, so a retry after a partial failure diffs against
   * what the server actually holds rather than re-offering codes that are already on.
   */
  taskAssignments: TaskAssignmentDto[] = [];

  /** The matter's tasks minus the ones already assigned, as picker options. */
  taskOptions: SelectOption[] = [];

  isLoadingTasks = false;

  /**
   * Set when the matter-task fetch fails. The section stays and keeps showing the tasks
   * already assigned — what is gone is any trustworthy list to pick from, so the picker
   * goes rather than offering an empty one that reads as "this matter has no tasks".
   */
  taskLoadFailed = false;

  /** Everything the feed returned, before the already-assigned filter. */
  private matterTasks: MatterTaskDto[] = [];

  /** Whether any task assignment was actually created in this dialog session. */
  private tasksCreated = false;

  /**
   * Whether the server has ever told us it holds a task this dialog never saw — the POST
   * answers with only the rows it created, so a skipped code never comes back and our
   * list is missing it. STICKY on purpose: that gap does not heal, so once it is set
   * buildResult must never report the list again, however many attempts follow. Making
   * this per-attempt is the subtle bug — a later attempt that skips nothing would clear
   * it and let the card repaint a count BELOW what the server holds.
   */
  private taskListKnownShort = false;

  /**
   * Whether THIS save attempt's create skipped codes / created rows. Both reset at the
   * top of save(), because they answer "what did this attempt just do" for the snackbar —
   * a question the sticky flags above cannot answer.
   */
  private tasksSkippedThisSave = false;
  private tasksCreatedThisSave = false;

  /**
   * Codes the server has told us it already holds. Filtered out of the picker so a
   * skipped code is not offered again on every subsequent attempt — the create for it
   * would be skipped again, forever.
   */
  private skippedTaskCodes = new Set<string>();

  /**
   * What the server holds, advanced by each call's own commit in save(). The date keys
   * are what isDirty and save() diff against; the Date pair beside them is the same
   * value in the shape buildResult reports, kept so the discard path can report what
   * committed without re-deriving a Date from a key.
   */
  private initialValue: {
    status: string;
    assignmentTypeCategory: string;
    startDate: string | null;
    endDate: string | null;
    startDateValue: Date | null;
    endDateValue: Date | null;
    notes: string;
    resourceNotes: string;
  };

  /** Whether any call in any save() attempt has landed during this dialog's life. */
  private committedWrite = false;
  private updatedNotesInfo: NotesWithMentionsDto | null | undefined;
  private updatedResourceNotesInfo: NotesWithMentionsDto | null | undefined;

  constructor() {
    const start = this.toDate(this.data.startDate);
    const end = this.toDate(this.data.endDate);
    const initialNotes = this.data.notesInfo?.notes ?? '';
    const initialResourceNotes = this.data.resourceNotesInfo?.notes ?? '';

    this.form = this.fb.group({
      status: [
        { value: this.data.currentStatus, disabled: !this.data.canEdit },
        Validators.required,
      ],
      assignmentTypeCategory: [
        {
          value: this.data.currentAssignmentType,
          disabled: !this.data.canEdit,
        },
        Validators.required,
      ],
      startDate: [{ value: start, disabled: !this.data.canEdit }],
      endDate: [{ value: end, disabled: !this.data.canEdit }],
      notes: [
        { value: initialNotes, disabled: !this.data.canEditManagerNotes },
      ],
      resourceNotes: [
        {
          value: initialResourceNotes,
          disabled: !this.data.canEditResourceNotes,
        },
      ],
      // Codes picked but not yet created. Behind the same Assignments.Edit gate as
      // status, dates and type; it never holds the tasks already assigned, which are
      // read-only here (removal is not in v1).
      newTaskCodes: [{ value: [] as string[], disabled: !this.data.canEdit }],
    });

    this.initialValue = {
      status: this.data.currentStatus,
      assignmentTypeCategory: this.data.currentAssignmentType,
      startDate: this.dateKey(start),
      endDate: this.dateKey(end),
      startDateValue: start,
      endDateValue: end,
      notes: initialNotes,
      resourceNotes: initialResourceNotes,
    };

    // The same order the assignments card's row menu uses (see the shared sort), so a
    // status reads in the same place on both surfaces.
    this.lookupService
      .getLookupOptions(LookupTypeCodes.ASSIGNED_RESOURCE_STATUS)
      .subscribe({
        next: (opts) => {
          this.statusOptions = sortAssignmentStatusOptions(opts).filter(
            (o) => !this.data.requestId || o.value.toUpperCase() !== 'PENDING',
          );
        },
      });

    this.lookupService.getLookupOptions('ASSIGNMENT_TYPE').subscribe({
      next: (opts) => (this.typeOptions = opts),
    });

    this.taskAssignments = [...(this.data.taskAssignments ?? [])];
    // Only when there is somewhere to put the answer: a hidden section has no picker,
    // and a read-only one offers nothing to pick.
    if (this.showTaskSection && this.data.canEdit) {
      this.loadMatterTasks();
    }

    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => this.close());
    this.dialogRef.keydownEvents().subscribe((e) => {
      if (e.key === 'Escape') this.close();
    });
  }

  get dialogTitle(): string {
    return this.data.dialogTitle || 'Assignment';
  }

  get clientMatterNumber(): string {
    return this.data.clientMatterNumber ?? '';
  }

  get resourceName(): string {
    return this.data.resourceName ?? '';
  }

  get currentStatusValue(): string {
    return (this.form?.get('status')?.value || this.data.currentStatus || '')
      .toString()
      .toUpperCase();
  }

  get currentStatusLabel(): string {
    const v = this.currentStatusValue;
    const match = this.statusOptions.find((o) => o.value.toUpperCase() === v);
    if (match) return match.label;
    if (!v) return '';
    return v.charAt(0) + v.slice(1).toLowerCase();
  }

  get statusBadgeClass(): string {
    switch (this.currentStatusValue) {
      case 'ACTIVE':
        return 'badge-active';
      case 'PENDING':
        return 'badge-pending';
      case 'INACTIVE':
        return 'badge-inactive';
      case 'CLOSED':
        return 'badge-closed';
      default:
        return 'badge-inactive';
    }
  }

  get dateRangeLabel(): string {
    const start = this.toDate(this.data.startDate);
    const end = this.toDate(this.data.endDate);
    if (!start && !end) return '';
    const fmt = (d: Date | null): string =>
      d
        ? d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '—';
    if (start && !end) return fmt(start);
    return `${fmt(start)} - ${fmt(end)}`;
  }

  get weeksLabel(): string {
    const start = this.toDate(this.data.startDate);
    const end = this.toDate(this.data.endDate);
    if (!start || !end) return '';
    const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    const weeks = Math.ceil(diffDays / 7);
    if (weeks <= 0) return '';
    return `${weeks} wk${weeks > 1 ? 's' : ''}`;
  }

  get minEndDate(): Date | null {
    const start = this.form?.get('startDate')?.value;
    return start instanceof Date ? start : null;
  }

  get endBeforeStart(): boolean {
    const start: Date | null = this.form.get('startDate')?.value ?? null;
    const end: Date | null = this.form.get('endDate')?.value ?? null;
    if (!start || !end) return false;
    return end.getTime() < start.getTime();
  }

  get hasDateError(): boolean {
    return this.endBeforeStart;
  }

  /**
   * Whether the date error should block saving. Only meaningful for date editors:
   * the pickers are disabled when canEdit is false, and the persisted dates can
   * already be end-before-start (the API resolves each bound independently,
   * falling back to the request's dates), so a resource-note-only editor would
   * otherwise be permanently unable to save a note they have every right to write
   * — with no way to correct the dates.
   */
  get blockingDateError(): boolean {
    return !!this.data.canEdit && this.hasDateError;
  }

  /**
   * The resource-note section renders whenever the owner is viewing (so they can
   * add a note), but for other viewers only when a note exists.
   */
  get showResourceNoteSection(): boolean {
    if (!this.data.showResourceNotes) return false;
    if (this.data.canEditResourceNotes) return true;
    return !!this.data.resourceNotesInfo?.notes;
  }

  /**
   * The Tasks section needs all three: the feature flag (dark launch), a matter to read
   * the available tasks from, and the assignment row the tasks hang off. Missing any one
   * leaves no trace of the section rather than a half-working one.
   */
  get showTaskSection(): boolean {
    return (
      !!this.data.showTaskAssignments &&
      !!this.data.clientMatterNumber &&
      !!this.data.assignedResourceId
    );
  }

  /**
   * Whether the section, once it is showing and its load has settled, offers a picker:
   * Assignments.Edit, and a load that did not fail. The template owns the other two
   * gates — this branch sits inside showTaskSection and after the isLoadingTasks one,
   * which shows a progress indicator instead (an empty picker would claim the matter
   * has no tasks before anyone knows).
   */
  get showTaskPicker(): boolean {
    return !!this.data.canEdit && !this.taskLoadFailed;
  }

  /**
   * What an empty picker means. taskOptions empties two ways: the matter really has no
   * tasks, or every task it returned is already on this assignment. Saying "none
   * available" for the second contradicts the read-only list rendered right above the
   * picker, so the unfiltered matterTasks decides the wording.
   */
  get taskPickerEmptyText(): string {
    return this.matterTasks.length > 0
      ? 'All of this matter’s tasks are already assigned'
      : 'No tasks available for this matter';
  }

  get resourceNoteLastUpdatedName(): string {
    // The shared helper returns undefined for "no name"; the template binds this
    // as a plain string, so the empty string is kept as this getter's contract.
    return (
      formatUserEditHistoryName(this.data.resourceNotesInfo?.lastUpdatedBy) ??
      ''
    );
  }

  get resourceNoteLastUpdatedTimestamp(): string | Date | undefined {
    return this.data.resourceNotesInfo?.lastUpdatedBy?.timestamp;
  }

  get isDirty(): boolean {
    const v = this.form.getRawValue();
    return (
      v.status !== this.initialValue.status ||
      v.assignmentTypeCategory !== this.initialValue.assignmentTypeCategory ||
      this.dateKey(v.startDate) !== this.initialValue.startDate ||
      this.dateKey(v.endDate) !== this.initialValue.endDate ||
      this.selectedTaskCodes(v).length > 0 ||
      this.managerNoteChanged(v) ||
      this.resourceNoteChanged(v)
    );
  }

  /**
   * The codes picked but not yet created. Shared by isDirty and save() for the same
   * reason the note comparisons are: a selection that enables Save must be the same
   * selection Save then sends.
   */
  private selectedTaskCodes(v: { newTaskCodes?: string[] | null }): string[] {
    return v.newTaskCodes ?? [];
  }

  /**
   * Loads the matter's tasks for the picker. Failure is reported and recorded rather
   * than retried: the dialog's other fields are unaffected, and the user can reopen it.
   *
   * Tied to the dialog's lifetime, because a matter lookup can outlive a dialog the user
   * dismissed straight away — and a snackbar about tasks for a dialog that is no longer
   * on screen belongs to nothing the user is looking at.
   *
   * A feed outage (503) carries the API's own wording, which says a retry may help;
   * anything else gets this dialog's copy.
   */
  private loadMatterTasks(): void {
    this.isLoadingTasks = true;
    this.matterService
      .getMatterTasks(this.clientMatterNumber)
      .pipe(
        takeUntil(this.dialogRef.afterClosed()),
        finalize(() => (this.isLoadingTasks = false)),
      )
      .subscribe({
        next: (tasks) => {
          this.matterTasks = tasks ?? [];
          this.refreshTaskOptions();
        },
        error: (error) => {
          this.taskLoadFailed = true;
          this.snackbar.showError(
            describeApiError(error, 'Failed to load tasks for this matter'),
          );
        },
      });
  }

  /**
   * The matter's tasks minus the ones this assignment already holds, recomputed after
   * every create so a code that just landed cannot be picked a second time. Compared
   * case-insensitively to match the API's duplicate check (OrdinalIgnoreCase): a
   * case-only difference between the feed and a stored row is the same task, and
   * offering it would only produce a create the server skips.
   */
  private refreshTaskOptions(): void {
    const assigned = new Set(
      this.taskAssignments.map((t) => t.taskCode.toUpperCase()),
    );
    this.taskOptions = this.matterTasks
      .filter(
        (t) =>
          !assigned.has(t.taskCode.toUpperCase()) &&
          !this.skippedTaskCodes.has(t.taskCode.toUpperCase()),
      )
      .map((t) => ({
        value: t.taskCode,
        label: t.taskDescription
          ? `${t.taskCode} — ${t.taskDescription}`
          : t.taskCode,
      }));
  }

  /**
   * Records the rows the server actually created. Only those: codes it skipped as
   * already assigned never created a row, so adding them here would invent a task with
   * no id. Nothing created means nothing to report to the card either (tasksCreated).
   */
  private applyCreatedTasks(
    created: TaskAssignmentDto[],
    submitted: string[],
  ): void {
    // The response carries only the rows the server made, so anything submitted and
    // missing from it was skipped as already assigned. Comparing the two is what catches
    // the PARTLY-skipped case: one new code and one duplicate comes back with a single
    // row, which on its own is indistinguishable from an ordinary create.
    const createdCodes = new Set(
      (created ?? []).map((t) => t.taskCode.toUpperCase()),
    );
    const skipped = submitted.filter((c) => !createdCodes.has(c.toUpperCase()));
    skipped.forEach((c) => this.skippedTaskCodes.add(c.toUpperCase()));
    this.tasksSkippedThisSave = skipped.length > 0;
    if (skipped.length > 0) this.taskListKnownShort = true;

    if (created?.length) {
      this.taskAssignments = [...this.taskAssignments, ...created];
      this.tasksCreated = true;
      this.tasksCreatedThisSave = true;
    }
    // Always: even a create that made nothing has to drop the skipped codes from the
    // picker, or they are offered again on the next attempt and skipped again.
    this.refreshTaskOptions();
  }

  /**
   * Whether the manager note differs from what was loaded. Trimmed, to match how
   * save() decides whether a call is needed — otherwise whitespace-only edits
   * enable Save and then no-op silently. Shared by isDirty and save() so the two
   * cannot drift apart.
   */
  private managerNoteChanged(v: { notes?: string | null }): boolean {
    return (v.notes ?? '').trim() !== this.initialValue.notes.trim();
  }

  /** Whether the resource note differs from what was loaded. See managerNoteChanged. */
  private resourceNoteChanged(v: { resourceNotes?: string | null }): boolean {
    return (
      (v.resourceNotes ?? '').trim() !== this.initialValue.resourceNotes.trim()
    );
  }

  /**
   * Treats a 404 from a note DELETE as success. The note is already gone, which is
   * the caller's intent — this matters for a stale dialog (the note was deleted in
   * another tab) and after a partial save failure, where the dialog stays open and
   * the user can retry.
   */
  private treat404AsSuccess<T>() {
    return recoverNotFound<T, null>(null);
  }

  save(): void {
    if (
      !this.data.canEdit &&
      !this.data.canEditManagerNotes &&
      !this.data.canEditResourceNotes
    ) {
      return;
    }
    if (this.form.invalid || this.blockingDateError) {
      this.form.markAllAsTouched();
      return;
    }

    const v = this.form.getRawValue();

    // Per-attempt, so it can only describe THIS save's own create. Without the reset it
    // survives a partial failure: attempt #1 submits a duplicate and fails on another
    // call, its commit clears the picker, and attempt #2 — carrying no codes at all —
    // still reports "those tasks were already assigned" and swallows the success of the
    // write that did land. skippedTaskCodes and taskListKnownShort are deliberately NOT
    // reset: what the server already holds stays true for the dialog's life.
    this.tasksSkippedThisSave = false;
    this.tasksCreatedThisSave = false;

    // Each call is paired with a commit that records what the server now holds, so a
    // retry after a partial failure re-diffs against reality rather than re-issuing a
    // write that already landed — for a DELETE that meant a guaranteed 404 dead-end,
    // with the surviving change impossible to save from the dialog.
    //
    // Each call absorbs its OWN failure into an {ok} flag instead of letting it reach
    // forkJoin. That is what makes the bookkeeping trustworthy: forkJoin otherwise
    // reports the first error and tears down while still having subscribed (and sent)
    // the remaining calls, whose results it then discards — so a write could land with no
    // way to observe that it had, leaving initialValue stale. With per-call capture every
    // call reports back, forkJoin always completes, and the subscriber below reports the
    // first failure it finds.
    const calls: Observable<{ ok: boolean; error?: unknown }>[] = [];
    const push = (
      factory: () => Observable<unknown>,
      onCommit: () => void,
    ): void => {
      calls.push(
        defer(factory).pipe(
          tap(() => {
            onCommit();
            // Marked here rather than inside each onCommit so every call is covered:
            // the discard path only needs to know that *a* write landed.
            this.committedWrite = true;
          }),
          map(() => ({ ok: true })),
          catchError((error) => of({ ok: false, error })),
        ),
      );
    };

    if (this.data.canEdit) {
      // Dates
      const newStartKey = this.dateKey(v.startDate);
      const newEndKey = this.dateKey(v.endDate);
      if (
        newStartKey !== this.initialValue.startDate ||
        newEndKey !== this.initialValue.endDate
      ) {
        push(
          () =>
            this.assignmentService.updateAssignmentDates(
              this.data.assignedResourceId,
              this.toIso(v.startDate),
              this.toIso(v.endDate),
            ),
          () => {
            this.initialValue.startDate = newStartKey;
            this.initialValue.endDate = newEndKey;
            this.initialValue.startDateValue = v.startDate ?? null;
            this.initialValue.endDateValue = v.endDate ?? null;
          },
        );
      }

      // Type
      if (
        v.assignmentTypeCategory !== this.initialValue.assignmentTypeCategory
      ) {
        push(
          () =>
            this.assignmentService.updateAssignmentType(
              this.data.assignedResourceId,
              v.assignmentTypeCategory,
            ),
          () => {
            this.initialValue.assignmentTypeCategory = v.assignmentTypeCategory;
          },
        );
      }

      // Status
      if (v.status !== this.initialValue.status) {
        push(
          () =>
            this.assignmentService.updateAssignmentStatus(
              this.data.assignedResourceId,
              v.status,
            ),
          () => (this.initialValue.status = v.status),
        );
      }

      // Tasks. Exactly one call for the whole selection: the API turns one call into one
      // notification (AC #4), so looping per code would notify the resource per task.
      // Clearing the control is the commit — a retry after some other call failed must
      // not re-send codes that already landed.
      const newTaskCodes = this.selectedTaskCodes(v);
      if (newTaskCodes.length > 0) {
        push(
          () =>
            this.assignmentService
              .createTaskAssignments(this.data.assignedResourceId, newTaskCodes)
              .pipe(
                tap((created) => this.applyCreatedTasks(created, newTaskCodes)),
                catchError((error) => {
                  // A rejected create means the cached feed offered a code the matter no
                  // longer has ("Task code(s) not available on matter"). Drop the entry so
                  // reopening the dialog refetches instead of offering it again — the
                  // cache otherwise lives as long as the SPA session.
                  if (this.data.clientMatterNumber) {
                    this.matterService.clearMatterTaskCache(
                      this.data.clientMatterNumber,
                    );
                  }
                  return throwError(() => error);
                }),
              ),
          () => this.form.get('newTaskCodes')?.setValue([]),
        );
      }
    }

    // Manager notes, gated on ManagerNotes.Write rather than canEdit (Assignments.Edit)
    // to match the API. Same upsert/delete/no-op branching as resource notes below: the
    // API rejects an empty PUT with 400, so clearing a note has to go through DELETE or
    // the user can never remove one.
    if (this.data.canEditManagerNotes) {
      const newNotes = (v.notes ?? '').toString().trim();
      if (this.managerNoteChanged(v)) {
        if (newNotes === '') {
          push(
            () =>
              this.notesService
                .deleteAssignmentNotes(this.data.assignedResourceId)
                .pipe(this.treat404AsSuccess()),
            () => {
              this.updatedNotesInfo = null;
              this.initialValue.notes = '';
            },
          );
        } else {
          push(
            () =>
              this.notesService
                .updateAssignmentNotes(this.data.assignedResourceId, newNotes)
                .pipe(tap((n) => (this.updatedNotesInfo = n))),
            () => (this.initialValue.notes = newNotes),
          );
        }
      }
    }

    // Resource notes (the current user's own note). The API rejects empty
    // PUTs and 404s on deleting a note that never existed, so compare
    // trimmed values and branch between upsert, delete, and no-op.
    if (this.data.canEditResourceNotes) {
      const newResourceNotes = (v.resourceNotes ?? '').toString().trim();
      if (this.resourceNoteChanged(v)) {
        if (newResourceNotes === '') {
          push(
            () =>
              this.notesService
                .deleteAssignmentResourceNotes(this.data.assignedResourceId)
                .pipe(this.treat404AsSuccess()),
            () => {
              this.updatedResourceNotesInfo = null;
              this.initialValue.resourceNotes = '';
            },
          );
        } else {
          push(
            () =>
              this.notesService
                .updateAssignmentResourceNotes(
                  this.data.assignedResourceId,
                  newResourceNotes,
                )
                .pipe(tap((n) => (this.updatedResourceNotesInfo = n))),
            () => (this.initialValue.resourceNotes = newResourceNotes),
          );
        }
      }
    }

    if (calls.length === 0) {
      this.dialogRef.close(this.buildResult());
      return;
    }

    this.isSaving = true;
    forkJoin(calls)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        // Every call reports here, successful or not; commits have already been applied
        // by each call's own tap, so initialValue reflects exactly what landed.
        next: (results) => {
          const failure = results.find((r) => !r.ok);
          if (failure) {
            this.snackbar.showError(
              describeApiError(failure.error, 'Failed to update assignment'),
            );
            return;
          }
          // A create the server skipped in full changed nothing, so an unqualified
          // "Assignment updated" would report work that did not happen — the picker
          // empties and the task list stays put, with no way to tell the save worked.
          // Reachable whenever the row's seeded tasks are behind the server: a second
          // user assigned the code, or this row was rendered before an earlier save.
          if (!this.tasksSkippedThisSave) {
            this.snackbar.showSuccess('Assignment updated');
          } else if (this.tasksCreatedThisSave || calls.length > 1) {
            // Something did land — either rows this create made, or another write in the
            // same batch — so it is a success, with the skip as the caveat.
            this.snackbar.showSuccess(
              'Assignment updated. Some tasks were already assigned.',
            );
          } else {
            // The create was the whole save and the server made nothing of it, so
            // nothing changed at all; a success tick would be a plain lie.
            this.snackbar.showInfo('Those tasks were already assigned.');
          }
          this.dialogRef.close(this.buildResult(v));
        },
      });
  }

  close(): void {
    if (!this.isDirty) {
      this.dialogRef.close(this.buildResult());
      return;
    }
    this.confirmDiscardForm();
  }

  private confirmDiscardForm(): void {
    const ref = this.dialog.open<
      UnsavedChangesDialogComponent,
      UnsavedChangesDialogData,
      string
    >(UnsavedChangesDialogComponent, {
      width: '440px',
      data: { entityName: 'Assignment' },
    });
    ref.afterClosed().subscribe((action) => {
      if (action === 'save') {
        this.save();
      } else if (action === 'discard') {
        // Discard drops the unsaved *form* edits, not writes that already landed. A save
        // can partially succeed — the task POST commits, another call fails, and the
        // dialog stays open for a retry — so closing with null here would leave the card
        // showing a stale task count for rows the server holds, and the resource already
        // notified about them. buildResult is fed initialValue rather than the form, so
        // only what committed is reported. Nothing committed still closes with null, which
        // is what keeps the card's `if (!result) return` short-circuiting a plain cancel.
        this.dialogRef.close(
          this.committedWrite ? this.buildResult(this.committedValue()) : null,
        );
      }
    });
  }

  /**
   * The status, type and dates as the server holds them — each one advanced only by its
   * own call's commit. Fed to buildResult on the discard path, where form.getRawValue()
   * would report edits the user just chose to throw away.
   */
  private committedValue(): {
    status: string;
    assignmentTypeCategory: string;
    startDate: Date | null;
    endDate: Date | null;
  } {
    return {
      status: this.initialValue.status,
      assignmentTypeCategory: this.initialValue.assignmentTypeCategory,
      startDate: this.initialValue.startDateValue,
      endDate: this.initialValue.endDateValue,
    };
  }

  private buildResult(formValue?: {
    status: string;
    assignmentTypeCategory: string;
    startDate: Date | null;
    endDate: Date | null;
  }): AssignmentEditDialogResult {
    const v = formValue ?? this.form.getRawValue();
    const statusLabel = this.statusOptions.find(
      (o) => o.value === v.status,
    )?.label;
    const typeLabel = this.typeOptions.find(
      (o) => o.value === v.assignmentTypeCategory,
    )?.label;
    return {
      status: v.status,
      statusName: statusLabel,
      assignmentTypeCategory: v.assignmentTypeCategory,
      assignmentTypeCategoryName: typeLabel,
      startDate: v.startDate ?? null,
      endDate: v.endDate ?? null,
      notesInfo: this.updatedNotesInfo,
      resourceNotesInfo: this.updatedResourceNotesInfo,
      // The whole list, so the card can drop it onto the row — but only when a row was
      // actually created AND nothing was skipped. A skip means the server holds a task
      // this dialog never saw (the POST returns only the rows it created, so a skipped
      // code never comes back), which makes the local list provably short; reporting it
      // would have the card repaint a row with a count lower than the truth. undefined
      // says "leave the row alone", which is stale but not wrong, and is also what tells
      // the card the dialog never touched tasks at all. A copy, so the card's row does
      // not alias the dialog's array.
      taskAssignments:
        this.tasksCreated && !this.taskListKnownShort
          ? [...this.taskAssignments]
          : undefined,
    };
  }

  private toDate(value?: Date | string | null): Date | null {
    if (!value) return null;
    if (value instanceof Date) return value;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  private toIso(value?: Date | null): string | null {
    if (!value) return null;
    return value.toISOString();
  }

  private dateKey(value?: Date | null): string | null {
    if (!value) return null;
    return value.toISOString().slice(0, 10);
  }
}
